import {
  CONTENT_DESCRIPTION_MAX_LENGTH,
  CONTENT_TITLE_MAX_LENGTH,
  CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH,
} from '@/constants/contentLimits';
import { validateArticleSections } from '@/modules/content/creation/model/articleValidation';
import { formatDomainLabel, getSubdomainsForDomain } from '@/constants/contentDomains';
import { TContentType } from '@/types/Content.types';
import { TContentFormErrors, TContentFormMode, TContentFormValues } from '@/modules/content/creation/model/types';
import { parseYouTubeId } from '@/modules/content/creation/model/youtube';

export const keepsExistingRemoteVideo = (values: TContentFormValues): boolean =>
  Boolean(
    values.hasOriginalRemoteVideo &&
      values.videoSource === values.originalVideoSource &&
      !values.videoFile &&
      (values.videoSource !== 'youtube' ||
        parseYouTubeId(values.youtubeUrl) === parseYouTubeId(values.originalYoutubeUrl ?? '')),
  );

// Step 1 gate: title, description, and at least one primary and one secondary domain are required to
// advance. For reels, a video source (upload or YouTube link) is also required here —
// the source field lives on this step. The remaining publish rules run at submit.
export const validateDetails = (values: TContentFormValues, type?: TContentType): TContentFormErrors => {
  const errors: TContentFormErrors = {};
  if (!values.title.trim()) {
    errors.title = 'Title is required.';
  } else if (values.title.length > CONTENT_TITLE_MAX_LENGTH) {
    errors.title = `Max ${CONTENT_TITLE_MAX_LENGTH} characters.`;
  }
  // For a post this field is the whole content — it is sent as both the description and
  // the body (see buildContentDetails), so the same rules cover both.
  if (!values.description.trim()) {
    errors.description = 'Description is required.';
  } else if (values.description.length > CONTENT_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Max ${CONTENT_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  if (values.domains.length === 0) {
    errors.domains = 'Select at least one primary domain.';
  }
  if (values.subdomains.length === 0) {
    errors.subdomains = 'Select at least one secondary domain.';
  } else {
    // The backend requires every selected domain to be represented, not just that the
    // chosen subdomains belong to the selection — so a domain left uncovered is rejected.
    const uncoveredDomains = values.domains.filter(
      (domain) => !getSubdomainsForDomain(domain).some((subdomain) => values.subdomains.includes(subdomain)),
    );
    if (uncoveredDomains.length > 0) {
      errors.subdomains = `Select at least one secondary domain for: ${uncoveredDomains
        .map(formatDomainLabel)
        .join(', ')}.`;
    }
  }
  if (type === 'video') {
    const keepsExistingVideo = keepsExistingRemoteVideo(values);
    const hasSource =
      keepsExistingVideo ||
      (values.videoSource === 'youtube'
        ? values.youtubeUrl.trim().length > 0
        : Boolean(values.videoFile || values.muxStatus));
    if (values.videoProcessingError && !keepsExistingVideo) {
      errors.video = values.videoProcessingError;
    } else if (!hasSource) {
      errors.video = values.videoSource === 'youtube' ? 'Add a YouTube link.' : 'Upload a video file.';
    }
  }
  return errors;
};

const validateArticle = (values: TContentFormValues, requireAll: boolean, errors: TContentFormErrors) => {
  const { fields, article } = validateArticleSections(values.sections, { requireAll });

  Object.assign(errors, fields);
  if (article) errors.sections = article;
};

const validateVideo = (values: TContentFormValues, _requireAll: boolean, errors: TContentFormErrors) => {
  // The video source requirement is enforced at the Details gate (see validateDetails);
  // here we only bound the full description length.
  if (values.fullDescription.length > CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH) {
    errors.fullDescription = `Max ${CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH} characters.`;
  }
};

const TYPE_VALIDATORS: Partial<
  Record<TContentType, (values: TContentFormValues, requireAll: boolean, errors: TContentFormErrors) => void>
> = {
  article: validateArticle,
  video: validateVideo,
};

// Step 2 gate. This step owns the body and the section fields, so it is the only place that
// can show a problem with them — by preview the creator is looking at a page that renders
// none of these inputs.
//
// Full rules here, required fields included: a creator should be told about an empty section
// title while they are looking at it, not three screens later. Saving an unfinished article
// is unaffected — "Save as Draft" sits in the wizard header and is reachable from any step,
// so this gate only holds back *moving forward*, never saving.
export const validateContentStep = (values: TContentFormValues, type: TContentType): TContentFormErrors => {
  const errors: TContentFormErrors = {};
  TYPE_VALIDATORS[type]?.(values, true, errors);
  return errors;
};

export const validateForSubmit = (
  values: TContentFormValues,
  type: TContentType,
  mode: TContentFormMode,
): TContentFormErrors => {
  const errors = validateDetails(values, type);
  const requireAll = mode === 'publish';

  TYPE_VALIDATORS[type]?.(values, requireAll, errors);

  return errors;
};

// True when the validation errors belong to step 1 (Details), so the wizard can jump back.
export const hasDetailsError = (errors: TContentFormErrors): boolean =>
  Boolean(errors.title || errors.description || errors.domains || errors.subdomains || errors.video);
